import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import PriceCalculator from './components/PriceCalculator';

const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://hornpmc.ca';

const pageSeo = {
  title: 'Deck & Fence Construction Burlington ON | Horn PMC',
  description:
    'Custom deck and fence builders serving Burlington, Oakville & Hamilton. Cedar decks, composite decks, vinyl fences, privacy fences. Free on-site estimates — call Horn PMC at 905-869-4676.',
  canonical: `${siteUrl}/decks-fences`,
  keywords: 'deck construction Burlington, fence installation Burlington, cedar deck Oakville, privacy fence Hamilton',
};

const serviceSchema = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Deck and Fence Construction',
  description:
    'Custom-built cedar decks, composite decks, vinyl fences, and privacy fences for Burlington, Oakville, and Hamilton homeowners.',
  provider: {
    '@type': 'LocalBusiness',
    name: 'Horn Property Maintenance and Construction',
    telephone: '+19058694676',
    url: siteUrl,
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Burlington',
      addressRegion: 'ON',
      addressCountry: 'CA',
    },
  },
  areaServed: ['Burlington, ON', 'Oakville, ON', 'Hamilton, ON'],
  serviceType: ['Deck Construction', 'Fence Installation', 'Outdoor Carpentry'],
  url: pageSeo.canonical,
  offers: {
    '@type': 'Offer',
    availability: 'https://schema.org/InStock',
    priceSpecification: {
      '@type': 'PriceSpecification',
      priceCurrency: 'CAD',
      description: 'Free on-site estimates available',
    },
  },
};

const webPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: pageSeo.title,
  description: pageSeo.description,
  url: pageSeo.canonical,
  isPartOf: {
    '@type': 'WebSite',
    name: 'Horn PMC',
    url: siteUrl,
  },
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
      { '@type': 'ListItem', position: 2, name: 'Decks & Fences', item: pageSeo.canonical },
    ],
  },
};

const galleryItems = [
  {
    id: 1,
    title: 'Cedar Privacy Fence',
    location: 'Burlington, ON',
    imageUrl:
      'https://public.readdy.ai/ai/img_res/06ffe381dae233bbe4092d3387f7663c.jpg',
  },
  {
    id: 2,
    title: 'Composite Deck with Pergola',
    location: 'Oakville, ON',
    imageUrl:
      'https://readdy.ai/api/search-image?query=Beautiful%20composite%20wood%20deck%20with%20white%20pergola%20structure%20outdoor%20furniture%20planters%2C%20residential%20backyard%20patio%20space%2C%20professional%20construction%20photography%2C%20warm%20summer%20sunlight%2C%20Oakville%20Ontario&width=600&height=450&seq=df2&orientation=landscape',
  },
  {
    id: 3,
    title: 'Two-Tier Cedar Deck',
    location: 'Burlington, ON',
    imageUrl:
      'https://readdy.ai/api/search-image?query=Two-level%20cedar%20wood%20deck%20with%20stairs%20railings%20outdoor%20seating%20in%20suburban%20backyard%2C%20natural%20wood%20tones%2C%20professional%20carpentry%20result%2C%20afternoon%20sunlight%2C%20Burlington%20Ontario%20residential%20backyard&width=600&height=450&seq=df3&orientation=landscape',
  },
  {
    id: 4,
    title: 'Vinyl Fence with Gates',
    location: 'Hamilton, ON',
    imageUrl:
      'https://readdy.ai/api/search-image?query=Clean%20white%20vinyl%20fence%20installation%20with%20double%20gate%20in%20suburban%20residential%20front%20yard%2C%20bright%20daylight%20photography%2C%20neat%20property%20boundary%2C%20professional%20fence%20construction%20Hamilton%20Ontario%20Canada&width=600&height=450&seq=df4&orientation=landscape',
  },
];

const features = [
  {
    icon: 'ri-shield-check-line',
    title: 'Quality Materials',
    desc: 'We source only premium-grade lumber, composite, and vinyl — materials that hold up to Ontario winters and look great year after year.',
  },
  {
    icon: 'ri-tools-line',
    title: 'Expert Craftsmanship',
    desc: 'Our experienced crew takes pride in every cut, joint, and post. Clean finishes and solid construction, every time.',
  },
  {
    icon: 'ri-calendar-check-line',
    title: 'On Time, On Budget',
    desc: 'We give you a clear timeline and stick to it. No surprise costs, no disappearing acts mid-project.',
  },
  {
    icon: 'ri-customer-service-2-line',
    title: 'Free Consultations',
    desc: 'Not sure what you need? We\'ll visit your property, assess the space, and walk you through all your options — no pressure.',
  },
];

export default function DecksFences() {
  return (
    <>
      <title>{pageSeo.title}</title>
      <meta name="description" content={pageSeo.description} />
      <meta name="keywords" content={pageSeo.keywords} />
      <link rel="canonical" href={pageSeo.canonical} />
      <meta property="og:title" content={pageSeo.title} />
      <meta property="og:description" content={pageSeo.description} />
      <meta property="og:url" content={pageSeo.canonical} />
      <meta property="og:type" content="website" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageSeo.title} />
      <meta name="twitter:description" content={pageSeo.description} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageSchema) }}
      />
      <main style={{ fontFamily: 'Inter, sans-serif' }}>
        <Navbar />

        {/* ── Hero ── */}
        <section className="relative w-full h-[520px] flex items-end pb-16">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Wide%20angle%20view%20of%20beautiful%20cedar%20wood%20deck%20with%20pergola%20railings%20and%20lush%20garden%20surrounding%20it%2C%20professional%20residential%20outdoor%20construction%20photography%2C%20Burlington%20Ontario%20backyard%2C%20warm%20golden%20hour%20sunlight%2C%20natural%20wood%20textures%2C%20high%20quality%20architectural%20photography&width=1920&height=600&seq=dfhero&orientation=landscape"
              alt="Deck and Fence Construction Burlington Ontario"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/30 to-black/20"></div>
          </div>

          {/* Breadcrumb */}
          <div className="absolute top-28 left-6 lg:left-16 flex items-center gap-2 text-white/70 text-xs">
            <a href="/" className="hover:text-white transition-colors">Home</a>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-white">Decks &amp; Fences</span>
          </div>

          <div className="relative z-10 w-full px-6 lg:px-16">
            <p className="text-[#C8A97E] text-xs font-bold tracking-[0.4em] uppercase mb-3">OUR SERVICES</p>
            <h1
              className="text-white text-5xl lg:text-6xl font-bold leading-tight mb-4"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Decks &amp; Fences
            </h1>
            <p className="text-white/75 text-base max-w-xl">
              Custom-built outdoor spaces crafted for Burlington, Oakville, and Hamilton homeowners.
            </p>
          </div>
        </section>

        {/* ── Intro + Features ── */}
        <section className="bg-white py-20 px-6">
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-14 items-center mb-16">
              <div className="flex-1">
                <p className="text-[#C8A97E] text-xs font-bold tracking-[0.4em] uppercase mb-4">WHY CHOOSE US</p>
                <h2
                  className="text-[#2B5741] text-3xl font-bold mb-5"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  Burlington's Deck &amp; Fence Specialists
                </h2>
                <p className="text-gray-600 text-sm leading-relaxed mb-5">
                  At Horn PMC, we specialize in crafting outdoor spaces that enhance your home's beauty and
                  functionality. Whether you're dreaming of a spacious cedar deck for summer entertaining or a
                  solid privacy fence to define your yard, our team delivers exceptional results with honest pricing.
                </p>
                <p className="text-gray-600 text-sm leading-relaxed">
                  We work closely with every homeowner to understand their vision, budget, and timeline — then
                  we build it right. No cutting corners, no vague estimates.
                </p>
              </div>
              <div className="lg:w-96 w-full h-72 rounded-xl overflow-hidden flex-shrink-0">
                <img
                  src="https://readdy.ai/api/search-image?query=Carpenter%20craftsman%20measuring%20and%20cutting%20cedar%20wood%20boards%20for%20deck%20construction%20project%2C%20professional%20tradesperson%20at%20work%2C%20residential%20backyard%20setting%2C%20natural%20daylight%2C%20high%20quality%20construction%20photography%20showing%20skilled%20craftsmanship&width=700&height=520&seq=dfintro&orientation=landscape"
                  alt="Expert deck and fence craftsmen Burlington Ontario"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            {/* Feature grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {features.map((f) => (
                <div key={f.title} className="flex gap-4 p-5 rounded-lg bg-[#F7F5F1]">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#2B5741] rounded-lg flex-shrink-0">
                    <i className={`${f.icon} text-white text-base`}></i>
                  </div>
                  <div>
                    <p className="text-gray-800 font-semibold text-sm mb-1">{f.title}</p>
                    <p className="text-gray-500 text-xs leading-relaxed">{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Price Calculator ── */}
        <PriceCalculator />

        {/* ── Gallery ── */}
        <section className="bg-white py-20 px-6">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <p className="text-[#C8A97E] text-xs font-bold tracking-[0.4em] uppercase mb-4">PAST WORK</p>
              <h2
                className="text-[#2B5741] text-4xl font-bold mb-3"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Recent Deck &amp; Fence Projects
              </h2>
              <p className="text-gray-500 text-sm">
                A selection of our completed projects across the Burlington area.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {galleryItems.map((item) => (
                <div key={item.id} className="group overflow-hidden rounded-lg">
                  <div className="w-full h-56 overflow-hidden">
                    <img
                      src={item.imageUrl}
                      alt={item.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="bg-[#F7F5F1] px-5 py-3.5">
                    <p className="text-gray-800 font-semibold text-sm">{item.title}</p>
                    <p className="text-gray-400 text-xs mt-0.5">{item.location}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── CTA Banner ── */}
        <section className="bg-[#2B5741] py-16 px-6">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-[#C8A97E] text-xs font-bold tracking-[0.4em] uppercase mb-4">READY TO START?</p>
            <h2
              className="text-white text-4xl font-bold mb-5"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Let's Build Something Great
            </h2>
            <p className="text-white/70 text-sm mb-8 max-w-lg mx-auto">
              Get a free, no-obligation on-site estimate. We serve Burlington, Oakville, Hamilton, and surrounding areas.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="/#contact"
                className="bg-[#C8A97E] text-white px-10 py-3.5 text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#b8976a] transition-colors whitespace-nowrap rounded-md"
              >
                GET A FREE QUOTE
              </a>
              <a
                href="tel:9058694676"
                className="flex items-center gap-2 border-2 border-white text-white px-10 py-3.5 text-xs font-bold uppercase tracking-[0.2em] hover:bg-white hover:text-[#2B5741] transition-colors whitespace-nowrap rounded-md"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-phone-fill"></i>
                </div>
                905-869-4676
              </a>
            </div>
          </div>
        </section>

        <Footer />
      </main>
    </>
  );
}